import { type QueryClient, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { createAgentClient } from "../../api/agentClient";
import type {
  PatientDemographicsUpdateInput,
  PatientProfile,
  PatientProfileSummary,
  PatientProfileUpdateInput,
} from "../../api/clinicalWorkspace";

/**
 * Query and mutation hooks for the durable patient record (20260904020142).
 *
 * One record, two views: the profile page and the encounter context panels
 * share these keys, so a write in either place invalidates the other. Every
 * write also invalidates the directory (counts), the legacy chart pane, and
 * the encounter workspace hydration so the in-visit rail catches up.
 */

/** Exact client surface the profile hooks need; tests pass a stub of this shape. */
export type PatientProfileClient = Pick<
  ReturnType<typeof createAgentClient>,
  | "getPatientProfile"
  | "getPatientProfileSummary"
  | "updatePatientProfile"
  | "updatePatientDemographics"
>;

export const patientProfileKey = (siteId: string, patientId: string) =>
  ["patient-profile", siteId, patientId] as const;

export const patientSummaryKey = (siteId: string, patientId: string) =>
  ["patient-summary", siteId, patientId] as const;

export const patientLabResultsKey = (siteId: string, patientId: string) =>
  ["patient-lab-results", siteId, patientId] as const;

/** Trends are a separate read model, so they cache apart from the row list. */
export const patientLabTrendsKey = (siteId: string, patientId: string) =>
  ["patient-lab-trends", siteId, patientId] as const;

export const patientLegacyVisitsKey = (siteId: string, patientId: string) =>
  ["patient-legacy-visits", siteId, patientId] as const;

export async function invalidatePatientQueries(
  queryClient: QueryClient,
  siteId: string,
  patientId: string,
): Promise<void> {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: patientProfileKey(siteId, patientId) }),
    queryClient.invalidateQueries({ queryKey: patientSummaryKey(siteId, patientId) }),
    queryClient.invalidateQueries({ queryKey: patientLabResultsKey(siteId, patientId) }),
    queryClient.invalidateQueries({ queryKey: patientLabTrendsKey(siteId, patientId) }),
    queryClient.invalidateQueries({ queryKey: patientLegacyVisitsKey(siteId, patientId) }),
    queryClient.invalidateQueries({ queryKey: ["patient-directory", siteId] }),
    queryClient.invalidateQueries({ queryKey: ["patient-chart", siteId, patientId] }),
    queryClient.invalidateQueries({ queryKey: ["patient-workspace", patientId] }),
    queryClient.invalidateQueries({ queryKey: ["patient-open-work", siteId, patientId] }),
  ]);
}

/** A fresh idempotency key; rotate it after every settled write, success or 409. */
export function newProfileIdempotencyKey(): string {
  return crypto.randomUUID();
}

export function usePatientProfileQuery(
  client: Pick<PatientProfileClient, "getPatientProfile">,
  siteId: string,
  patientId: string | null,
  options: { readonly enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: patientProfileKey(siteId, patientId ?? ""),
    queryFn: ({ signal }) => client.getPatientProfile(siteId, patientId ?? "", signal),
    enabled: patientId !== null && (options.enabled ?? true),
    // A 403 means the role cannot see this chart; a 404 means it is gone.
    // Neither improves with retries, and the page renders both explicitly.
    retry: false,
  });
}

export interface ProfileSectionSave {
  readonly idempotencyKey: string;
  readonly expectedUpdatedAt: string;
  readonly profile: PatientProfileUpdateInput;
}

export function useUpdatePatientProfile(
  client: Pick<PatientProfileClient, "updatePatientProfile">,
  siteId: string,
  patientId: string,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (save: ProfileSectionSave) =>
      client.updatePatientProfile(patientId, { siteId, ...save }),
    onSuccess: async (receipt) => {
      queryClient.setQueryData<PatientProfile>(
        patientProfileKey(siteId, patientId),
        receipt.profile,
      );
      await invalidatePatientQueries(queryClient, siteId, patientId);
    },
  });
}

export interface DemographicsSave {
  readonly idempotencyKey: string;
  readonly demographics: PatientDemographicsUpdateInput;
}

export function useUpdatePatientDemographics(
  client: Pick<PatientProfileClient, "updatePatientDemographics">,
  siteId: string,
  patientId: string,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (save: DemographicsSave) =>
      client.updatePatientDemographics(patientId, { siteId, ...save }),
    onSuccess: async (receipt) => {
      queryClient.setQueryData<PatientProfile>(
        patientProfileKey(siteId, patientId),
        receipt.profile,
      );
      await invalidatePatientQueries(queryClient, siteId, patientId);
    },
  });
}

/**
 * The profile hero's summary. Enabled only once the profile proves the role
 * sees clinical sections: identity-only roles get a 403 here and keep the
 * client-built facts instead. A miss generates on the server, so a first
 * load can take a few seconds; the card shows chart facts meanwhile.
 */
export function usePatientSummaryQuery(
  client: Pick<PatientProfileClient, "getPatientProfileSummary">,
  siteId: string,
  patientId: string | null,
  options: { readonly enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: patientSummaryKey(siteId, patientId ?? ""),
    queryFn: ({ signal }) => client.getPatientProfileSummary(siteId, patientId ?? "", {}, signal),
    enabled: patientId !== null && (options.enabled ?? true),
    retry: false,
  });
}

/** "Refresh": re-read the chart and regenerate when it moved; the result replaces the cached summary. */
export function useRefreshPatientSummary(
  client: Pick<PatientProfileClient, "getPatientProfileSummary">,
  siteId: string,
  patientId: string,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => client.getPatientProfileSummary(siteId, patientId, { refresh: true }),
    onSuccess: (summary) => {
      queryClient.setQueryData<PatientProfileSummary>(
        patientSummaryKey(siteId, patientId),
        summary,
      );
    },
  });
}
